import {
    type IncomeSplitRuleLegacy,
    IncomeSplitRuleType,
} from '@fruitful/income-split';
import { Redirect, router } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useAtom, useAtomValue } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { redactPrivate, ScreenContainer, XStack, YStack } from 'smoothie';

import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import useGetAllBankAccountDetailsFromGlobalState from '../../hooks/useGetAllBankAccountDetailsFromGlobalState';
import useGetInternalAndExternalDepositAccounts from '../../hooks/useGetInternalAndExternalDepositAccounts';
import {
    incomeSplitAtomLegacy,
    incomeSplitRuleEditorModeAtom,
    selectedIncomeRuleAtomLegacy,
} from '../../state/PaycheckRoutingAtoms';
import RuleEditorButtons from '../paycheck-routing/income-account-routing/rule-editor/common/RuleEditorButtons';
import RuleEditorAccount from './editor/RuleEditorAccount';
import RuleEditorAmount from './editor/RuleEditorAmount';
import RuleEditorContributionType from './editor/RuleEditorContributionType';
import RuleEditorPercentage from './editor/RuleEditorPercentage';
import IncomeSplitContributionSheet from './IncomeSplitContributionLimitInfoSheet';
import IncomeSplitTargetBalanceInfoSheet from './IncomeSplitTargetBalanceInfoSheet';

const IncomeSplitRuleEditor = () => {
    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtomLegacy);
    const { bankAccountDetails } = useGetAllBankAccountDetailsFromGlobalState();
    const [selectedRule, setSelectedRule] = useAtom(
        selectedIncomeRuleAtomLegacy,
    );
    const incomeSplitRuleEditorMode = useAtomValue(
        incomeSplitRuleEditorModeAtom,
    );

    const [isRuleValid, setIsRuleValid] = useState(false);
    const [isTargetBalanceInfoSheetOpen, setIsTargetBalanceInfoSheetOpen] =
        useState(false);
    const [
        isContributionLimitInfoSheetOpen,
        setIsContributionLimitInfoSheetOpen,
    ] = useState(false);
    const [contributionMaxType, setContributionMaxType] = useState<
        'Monthly' | 'Annual' | null
    >(null);
    const [maxContributionRowWidth, setMaxContributionRowWidth] = useState(0);

    const { data: depositAccounts } = useGetInternalAndExternalDepositAccounts(
        {},
    );

    const externalAccounts = (depositAccounts ?? []).filter(
        (d) => d.isExternal,
    );

    const {
        amount,
        accountId,
        type,
        percentage,
        monthlyContributionMaxCents,
        yearlyContributionMaxCents,
    } = selectedRule ?? {};

    useEffect(() => {
        let isValid = false;

        switch (selectedRule?.type as IncomeSplitRuleType) {
            case IncomeSplitRuleType.MINIMUM_BALANCE:
                isValid =
                    (amount ?? 0) > 0 &&
                    percentage !== 0 &&
                    accountId != null &&
                    type != null;
                break;
            case IncomeSplitRuleType.PERCENTAGE:
                isValid = percentage !== 0 && accountId != null && type != null;
                break;
        }

        setIsRuleValid(isValid);
    }, [selectedRule]);

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    // if the member is adding a new rule, lets prompt them to select the destination account first
    useEffect(() => {
        if (incomeSplitRuleEditorMode === 'Add') {
            router.navigate('/income-split/edit/account');
        }
    }, []);

    if (
        !shouldSeeIncomeSplit ||
        !incomeSplit ||
        bankAccountDetails.length < 1 ||
        !selectedRule ||
        !incomeSplitRuleEditorMode
    ) {
        return <Redirect href="/" />;
    }

    const finishEditing = () => {
        router.back();
        setSelectedRule(null);
    };

    return (
        <>
            <StatusBar style="light" />
            <ScreenContainer
                title={`${incomeSplitRuleEditorMode} rule`}
                hasCloseButton={true}
                onClose={() => {
                    finishEditing();
                }}
                useSafeAreaInsetTop={false}
            >
                <YStack height="100%" gap={24} justifyContent="space-between">
                    <YStack gap={16} {...redactPrivate()}>
                        <>
                            <RuleEditorAccount
                                bankAccounts={bankAccountDetails}
                                externalAccounts={externalAccounts}
                                rule={selectedRule ?? {}}
                                cardProps={{ paddingVertical: 11 }}
                            />
                            <RuleEditorContributionType
                                percentage={percentage}
                            />
                            {percentage != null && (
                                <RuleEditorPercentage
                                    amount={
                                        percentage < 1
                                            ? Math.floor(percentage * 100)
                                            : percentage
                                    }
                                    updateAmount={(amount) => {
                                        setSelectedRule({
                                            ...selectedRule,
                                            percentage: amount,
                                        });
                                    }}
                                    label="Percentage"
                                />
                            )}
                            <RuleEditorAmount
                                amount={
                                    (type as IncomeSplitRuleType) ===
                                    IncomeSplitRuleType.PERCENTAGE
                                        ? 0
                                        : amount
                                }
                                disabled={selectedRule.isExternal}
                                updateAmount={(amount) => {
                                    setSelectedRule({
                                        ...selectedRule,
                                        amount: amount > 0 ? amount : 100,
                                        type:
                                            amount > 0
                                                ? IncomeSplitRuleType.MINIMUM_BALANCE
                                                : IncomeSplitRuleType.PERCENTAGE,
                                    });
                                }}
                                label="Target balance (optional)"
                                openInfoSheet={() => {
                                    setIsTargetBalanceInfoSheetOpen(true);
                                }}
                            />
                            <XStack
                                width="100%"
                                gap={16}
                                onLayout={(e) => {
                                    setMaxContributionRowWidth(
                                        e.nativeEvent.layout.width,
                                    );
                                }}
                            >
                                <RuleEditorAmount
                                    amount={monthlyContributionMaxCents}
                                    updateAmount={(amount) => {
                                        setSelectedRule({
                                            ...selectedRule,
                                            monthlyContributionMaxCents: amount,
                                        });
                                    }}
                                    label="Monthly limit (optional)"
                                    openInfoSheet={() => {
                                        setContributionMaxType('Monthly');
                                        setIsContributionLimitInfoSheetOpen(
                                            true,
                                        );
                                    }}
                                    width={maxContributionRowWidth / 2 - 8}
                                />
                                <RuleEditorAmount
                                    amount={yearlyContributionMaxCents}
                                    updateAmount={(amount) => {
                                        setSelectedRule({
                                            ...selectedRule,
                                            yearlyContributionMaxCents: amount,
                                        });
                                    }}
                                    label="Annual limit (optional)"
                                    openInfoSheet={() => {
                                        setContributionMaxType('Annual');
                                        setIsContributionLimitInfoSheetOpen(
                                            true,
                                        );
                                    }}
                                    width={maxContributionRowWidth / 2 - 8}
                                />
                            </XStack>
                        </>
                    </YStack>

                    <RuleEditorButtons
                        onSave={() => {
                            if (incomeSplitRuleEditorMode === 'Edit') {
                                setIncomeSplit({
                                    ...incomeSplit,
                                    rules: (incomeSplit?.rules ?? []).map(
                                        (rule) => {
                                            return rule.id === selectedRule.id
                                                ? (selectedRule as IncomeSplitRuleLegacy)
                                                : rule;
                                        },
                                    ),
                                });
                            } else {
                                setIncomeSplit({
                                    ...incomeSplit,
                                    rules: [
                                        ...(incomeSplit?.rules ?? []),
                                        selectedRule as IncomeSplitRuleLegacy,
                                    ],
                                });
                            }
                            finishEditing();
                        }}
                        onCancel={finishEditing}
                        disableSave={!isRuleValid}
                    />
                </YStack>
                <IncomeSplitContributionSheet
                    isOpen={isContributionLimitInfoSheetOpen}
                    onClose={() => {
                        setIsContributionLimitInfoSheetOpen(false);
                        setContributionMaxType(null);
                    }}
                    contributionMaxType={contributionMaxType}
                />
                <IncomeSplitTargetBalanceInfoSheet
                    isOpen={isTargetBalanceInfoSheetOpen}
                    onClose={() => {
                        setIsTargetBalanceInfoSheetOpen(false);
                    }}
                />
            </ScreenContainer>
        </>
    );
};

export default IncomeSplitRuleEditor;
